import React, { useRef, useState, useEffect, useCallback } from 'react';

import moment, { Moment } from 'moment';
import { DayPicker } from 'react-day-picker';
import { useIntl, FormattedHTMLMessage, FormattedMessage, IntlProvider } from 'react-intl';
import styled, { css } from 'styled-components';

import { KEYS, DAY_PICKER_CLASS_NAMES } from '../../../../../../../../constants/Common';

import translateHookTS from '../../../../../../../Hooks/Lang/TranslateHookTS';
import connectAuth, { connectIntlAuth, IAuthMapStateToProps, IConnectedProps } from '../../../../hooks/connectAuth';

import Button from '../../../../../Common/Form/Button';
import Popover from '../../../../../Common/Form/Popover';
import Select, { Option } from '../../../../../Common/Form/Select';
import { MAX_AGE, MIN_AGE } from '../../../../Constants';
import { multipleActions } from '../../../../Store/ContainerReducer';
import { IAuthState } from '../../../../Store/Reducers';
import { Types, initialState as reducerInitialState, IKeyDown } from '../../../../Store/SignUpReducer';

interface IDatePickerProps {
    date: Moment | null,
    reset: boolean,
    maxDate: Moment,
    minDate: Moment,
    years: string[],
    months: string[],
    keyDown?: IKeyDown,
    selectedDay: string,
    selectedYear: string,
    selectedMonth: string,
    openDatePicker: boolean,
    datePickerDate: Moment | null
}

interface IDatePickerAttributes {
    onDateChanged: (newDate: moment.Moment) => void
}

type TDatePickerProps = IConnectedProps<IDatePickerProps> & IDatePickerAttributes;

const initialState = reducerInitialState.SignUp.birthData.birthDate.datePickerState;

const KEYS_WHITELIST = [KEYS.ESC, KEYS.ENTER];

interface IContainer {
    dayPickerDisabled: boolean
}

const disabledDay = css<IContainer>`
    pointer-events: none;
    padding: ${({ theme }) => theme.space.bee};
    color: ${({ theme }) => theme.colors.gray300};
    background-color: ${({ theme }) => theme.colors.gray200};

    &--selected {
        color: ${({ theme }) => theme.colors.gray300};
        background-color: ${({ theme }) => theme.colors.gray200};
    }
`;

const regularDay = css<IContainer>`
    pointer-events: 'inherit';
    color: ${({ theme }) => theme.colors.gray600};
    background-color: ${({ theme }) => theme.colors.white};

    &--selected {
        color: ${({ theme }) => theme.colors.onPrimary};
        background-color: ${({ theme }) => theme.colors.primary};
    }
`;

const Container = styled.div<IContainer>`
    > p {
        color: ${({ theme }) => theme.colors.black};
        padding: 0 ${({ theme }) => theme.space.mouse};
        font-size: ${({ theme }) => theme.fontSizes.cat};
        margin-bottom: ${({ theme }) => theme.space.mouse};
        font-weight: ${({ theme }) => theme.fontWeights.black};

        > span {
            display: flex;
            align-items: center;
            flex-direction: column;
        }
    }      

    .DayPicker {
        margin: ${({ theme }) => theme.space.mouse} 0;
    }

    .DayPicker-NavBar,
    .DayPicker-Caption,
    .DayPicker-Weekdays {
        display: none;
    }

    .DayPicker-Body {
        display: table-row-group;
    }

    .DayPicker-Week {
        display: table-row;
    }

    .DayPicker-Month,
    .DayPicker-wrapper {
        outline: none;
        border-spacing: 0;
        border-collapse: collapse;
    }

    .DayPicker-Cell {
        padding: 0;
        vertical-align: middle;
        border: 0.0625em solid ${({ theme }) => theme.colors.gray300};

        &:not(:has(.DayPicker-Day)) {
            border: none;
        }

        ${({ dayPickerDisabled }) => dayPickerDisabled && css`opacity: .5;`};
    }

    .DayPicker-Day {
        width: 100%;
        border: none;
        display: block;
        font: inherit;
        appearance: none;
        cursor: pointer;
        text-align: center;
        padding: ${({ theme }) => theme.space.bee};

        &:focus {
            outline: none;
        }

        ${({ dayPickerDisabled }) => dayPickerDisabled ? disabledDay : regularDay};
    }
`;

const SelectContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.mouse};

    > button {
        &:nth-of-type(1) {
            flex: 1;
        }

        &:nth-of-type(2) {
            flex: 2;
        }
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    margin: 0.9375em 0 0;
    gap: ${({ theme }) => theme.space.mouse};

    > ${Button} {
        flex: 1;
    }
`;

const CANCEL_BUTTON_ID = 'cancel-date-picker';
const CONFIRM_BUTTON_ID = 'confirm-date-picker';

const monthsArray = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

const DatePicker: React.FC<TDatePickerProps> = ({ dispatch, ...props }) => {
    const intl = useIntl();
    const selectYearRef = useRef<HTMLButtonElement>(null);
    const [disableCancelButton, setDisableCancelButton] = useState(false);

    const {
        date,
        years,
        reset,
        months,
        minDate,
        maxDate,
        keyDown,
        selectedDay,
        selectedYear,
        selectedMonth,
        onDateChanged,
        datePickerDate,
        openDatePicker
    } = props;

    const normalizeNumber = (val: number) => (`0${val}`).slice(-2);

    const getLocalDate = (day = '01', month = '01', year = selectedYear) => moment(`${year}-${month}-${day}`);

    const handleChangeDay = (newDay: Date): void => {
        if (!selectedMonth) return;

        const normalizedDay = normalizeNumber(newDay.getDate());
        const newDate = getLocalDate(normalizedDay, selectedMonth, selectedYear);

        dispatch({
            type: Types.CHANGE_DATE_PICKER_STATE,
            data: {
                datePickerDate: newDate,
                selectedDay: normalizedDay
            }
        });
    };

    const handleChangeMonth = (value: string) => {
        setDisableCancelButton(true);
        const month = normalizeNumber(parseInt(value));

        dispatch({
            type: Types.CHANGE_DATE_PICKER_STATE,
            data: {
                selectedDay: null,
                selectedMonth: month,
                datePickerDate: getLocalDate(undefined, month, undefined)
            }
        });

        setTimeout(() => {
            setDisableCancelButton(false);
        }, 300);
    };

    const handleChangeYear = (newYear: string) => {
        setDisableCancelButton(true);
        dispatch({
            type: Types.CHANGE_DATE_PICKER_STATE,
            data: {
                months: (newYear === minDate.format('YYYY'))
                    ? monthsArray.slice(0, minDate.month() + 1)
                    : monthsArray,
                selectedYear: newYear
            }
        });

        setTimeout(() => {
            setDisableCancelButton(false);
        }, 300);
    };

    const handleClickCancel = useCallback(() => {
        if (!date) {
            dispatch({
                type: Types.CHANGE_DATE_PICKER_STATE,
                data: {
                    selectedYear: '',
                    selectedMonth: '',
                    selectedDay: null
                }
            });
        }

        dispatch({
            type: Types.CHANGE_DATE_DATA,
            data: { openDatePicker: false }
        });

        const input = document.getElementById('input-birth-date-field');
        if (!input) return;

        input.focus();
    }, [date, dispatch]);

    const updateDateValue = useCallback(() => {
        const isAllSelected = selectedYear !== '' && selectedMonth !== '' && selectedDay !== '';

        if (!(isAllSelected && datePickerDate)) return false;

        onDateChanged(datePickerDate);

        return true;
    }, [datePickerDate, onDateChanged, selectedDay, selectedMonth, selectedYear]);

    const handleClickConfirm = useCallback(() => {
        const dateChanged = updateDateValue();

        if (!dateChanged) return;

        dispatch({
            type: Types.CHANGE_DATE_DATA,
            data: { openDatePicker: false }
        });

        const input = document.getElementById('input-birth-date-field');
        if (!input) return;

        input.focus();
    }, [dispatch, updateDateValue]);

    useEffect(() => {
        if (!keyDown || keyDown && !keyDown.key) return;

        const currentId = document.activeElement?.id;
        const isEscapePressed = keyDown.key === KEYS.ESC;
        const isEnterPressed = keyDown.key === KEYS.ENTER;

        const hasFocus = document
            .getElementById('popover-date-picker')
            ?.contains(document.activeElement);

        if (!hasFocus) dispatch({ type: Types.RESET_KEYDOW });

        if (!(hasFocus && openDatePicker)) return;

        if (isEscapePressed || (isEnterPressed && currentId === CANCEL_BUTTON_ID)) {
            handleClickCancel();
            return;
        }

        if (currentId !== CONFIRM_BUTTON_ID) return;

        updateDateValue();

        multipleActions(dispatch, [
            { type: Types.RESET_KEYDOW },
            {
                type: Types.CHANGE_DATE_DATA,
                data: { openDatePicker: false }
            }
        ]);
    }, [keyDown, openDatePicker, dispatch, updateDateValue, handleClickCancel]);

    useEffect(() => {
        if (!selectYearRef.current || !openDatePicker) return;

        selectYearRef.current.focus();
    }, [dispatch, openDatePicker]);

    useEffect(() => {
        const newYears: string[] = [];
        const localMaxDate = moment(maxDate);

        for (let age = MIN_AGE; age <= MAX_AGE; age++) {
            newYears.push(localMaxDate.format('YYYY'));
            localMaxDate.add(1, 'year');
        }

        const reverseYears = [...newYears].reverse();

        dispatch({
            type: Types.CHANGE_DATE_PICKER_STATE,
            data: { years: reverseYears }
        });
    }, [maxDate, dispatch]);

    useEffect(() => {
        if (!reset) return;

        dispatch({
            type: Types.CHANGE_DATE_PICKER_STATE,
            data: { ...initialState }
        });
    }, [reset, dispatch]);

    const localDate = datePickerDate ? datePickerDate?.toDate() : moment().toDate();

    return (
        <Popover
            modal
            id='date-picker'
            open={openDatePicker}
        >
            <Container
                data-cy="date-picker-container"
                dayPickerDisabled={!selectedMonth}
            >
                <p><FormattedHTMLMessage id="authFunctional.datePicker.title" /></p>

                <SelectContainer>
                    <Select
                        id="birth-year"
                        ref={selectYearRef}
                        value={selectedYear}
                        onValueChange={handleChangeYear}
                        tabIndex={openDatePicker ? 0 : -1}
                        placeholder={intl.formatMessage({ id: 'authFunctional.datePicker.year' })}
                    >
                        {years.map(item => <Option value={item} key={item}>{item}</Option>)}
                    </Select>

                    <Select
                        open
                        id="birth-month"
                        value={selectedMonth}
                        disabled={!selectedYear}
                        onValueChange={handleChangeMonth}
                        tabIndex={openDatePicker ? 0 : -1}
                        placeholder={intl.formatMessage({ id: 'authFunctional.datePicker.month' })}
                    >
                        {months.map((item, key) => (
                            <Option value={normalizeNumber(key + 1)} key={item}>
                                {intl.formatMessage({ id: `authFunctional.datePicker.months.${item}` })}
                            </Option>
                        ))}
                    </Select>
                </SelectContainer>

                {/*
                  * `minDate`/`maxDate` são nomeados pela IDADE, não pela data: `minDate` é
                  * hoje-13anos (a data MAIS RECENTE aceita) e `maxDate` é hoje-120anos (a MAIS
                  * ANTIGA). O limite antigo é o `fromMonth` e o recente é o `toMonth`. Trocar
                  * os dois inverte o intervalo e o v8 desabilita TODOS os dias — ele deriva
                  * `disabled: [{before: fromDate}, {after: toDate}]`, o que no v7 não existia.
                  */}
                <DayPicker
                    mode="single"
                    weekStartsOn={0}
                    month={localDate}
                    selected={selectedDay ? localDate : undefined}
                    toMonth={minDate.toDate()}
                    fromMonth={maxDate.toDate()}
                    onDayClick={handleChangeDay}
                    classNames={DAY_PICKER_CLASS_NAMES}
                />

                <ButtonGroup>
                    <Button
                        tabIndex={0}
                        variation="outlined"
                        id="cancel-date-picker"
                        onClick={handleClickCancel}
                        disabled={disableCancelButton}
                    >
                        <FormattedMessage id="authFunctional.modalButtons.cancel" />
                    </Button>

                    <Button
                        disabled={!selectedDay}
                        id="confirm-date-picker"
                        onClick={handleClickConfirm}
                        tabIndex={selectedDay ? 0 : -1}
                    >
                        <FormattedMessage id="authFunctional.modalButtons.continue" />
                    </Button>
                </ButtonGroup>
            </Container>
        </Popover>
    );
};

const mapStateToProps: IAuthMapStateToProps<IDatePickerProps> = (state, isComparison = false) => (!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        date: (state as IAuthState).SignUp.birthData.birthDate.date,
        maxDate: (state as IAuthState).SignUp.birthData.birthDate.maxDate,
        minDate: (state as IAuthState).SignUp.birthData.birthDate.minDate,
        years: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.years,
        openDatePicker: (state as IAuthState).SignUp.birthData.birthDate.openDatePicker,
        months: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.months,
        selectedDay: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.selectedDay,
        selectedYear: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.selectedYear,
        selectedMonth: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.selectedMonth,
        datePickerDate: (state as IAuthState).SignUp.birthData.birthDate.datePickerState.datePickerDate,
        keyDown: (KEYS_WHITELIST.indexOf((state as IAuthState).SignUp.keyDown.key) !== -1) ? (state as IAuthState).SignUp.keyDown : undefined
    } : {
        date: state.date,
        reset: state.reset,
        years: state.years,
        months: state.months,
        keyDown: state.keyDown,
        maxDate: state.maxDate,
        minDate: state.minDate,
        selectedDay: state.selectedDay,
        selectedYear: state.selectedYear,
        selectedMonth: state.selectedMonth,
        datePickerDate: state.datePickerDate,
        openDatePicker: state.openDatePicker
    };

export default connectIntlAuth<IDatePickerProps, IDatePickerAttributes>(mapStateToProps)(DatePicker);
